// wiring.ts — 데이터·API 배선(개발자 소유). 컨덕터는 이 파일이 없을 때 한 번 만들고 다시 건드리지 않는다.
//  화면 파일은 컨덕터가 재핸드오프 때 통째로 덮어쓴다 — 배선은 여기에만 둔다. 계약(Wiring)은 화면 파일이 낸다(문서 50).
//  최초 골격: pbo-conductor — GPC 배송비 분담금 단가 관리 (gpc-rate)
//  옛 손코드 화면을 옮길 때 리뷰에 걸린 것들(moms-fe #770, 2026-09-18) — 초안을 쓰기 전에 하나씩 확인:
//   ① 페이저 크기 vs BE 최대 size(데모 10,000 ↔ BE 1,000 이면 청크로 이어 받는다) ② 저장 부분 실패(HTTP 200 + 실패 행)는 요청 실패와 나눠 토스트 1개 + 재조회
//   ③ 모달 폼 필수값(사유코드·상세 글자 수)은 form.<모달> 슬롯이 올린 값으로 <모달>_ok 에서 검증(실패 = throw, 모달 유지) ④ URL/세션 복원 조건은 filterDefaults 로 조회영역에도 보이게
//   ⑤ 모듈 변수(편집 행·잠금)는 마운트 때 비운다 ⑥ 같은 조건 재조회는 queryKey 가 같아 요청이 안 나간다 — 무효화(invalidateQueries)로 ⑦ 같은 훅을 슬롯 두 곳에서 부르지 않는다(한 곳 + 모듈 변수)
//
//  이 화면의 배선 — MASS 매트릭스(GET /api2/mass/v1/cost-share/prices/matrix) + 셀 단건 저장(POST …/prices)
//   + 엑셀 양식·조회 결과 다운로드(GET …/excel-template · …/excel-download) + 엑셀 업로드(POST …/excel-validation → …/excel-upload)
//   + 변경 이력(GET …/histories).
//   · 엑셀 업로드는 두 단계가 같은 파일을 각각 보낸다(서버는 검증 결과를 보관하지 않는다). 업로드 모달 [업로드] = 검증, 검증 모달 [저장] = 확정 저장.
//     파일·검증 결과는 모듈 변수에 두고, 검증 모달이 다시 그려지도록 ROWS2 의 상태 setter 로 올린다(409 로 새 검증 결과가 오면 그걸로 다시 그린다).
//   · 표 슬롯(rows.ROWS)만 훅을 부른다(생성본이 본문 첫머리에서 매 렌더 같은 순서로 부른다). 액션 슬롯은 이벤트에서 불려 훅을 못 쓰므로
//     ROWS 가 채운 모듈 변수(조회 조건 커밋·queryClient·저장 mutate·선택 셀)를 읽는다(⑦). 마운트/언마운트 때 비운다(⑤).
//   · 페이저 크기는 stateInitial 로 BE 상한(1,000) 안의 값을 준다 — 청크 이어받기 대신 진짜 서버 페이지네이션(①).
import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { isEqual } from 'lodash-es'
import MAlert from '@/utils/modal/MAlert'
import MConfirm from '@/utils/modal/MConfirm'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { Utils } from '@/utils/Utils'
import { useGetLogisticsCategories } from '@/services/commonCode/useCommonCodeQueries'
import {
  downloadGpcRateExcel,
  gpcRateExcelSearchResultUrl,
  gpcRateExcelTemplateUrl,
  uploadGpcRateExcel,
  validateGpcRateExcel,
} from '@/services/global/gpcRate/apis'
import {
  GPC_RATE_COUNTRY_GROUP_OPTIONS,
  GPC_RATE_FILTER_KEYS,
  gpcRateExcelFallbackName,
  gpcRateUnregisteredWarningCount,
  gpcRateUploadSaveBlock,
  gpcRateZeroPriceCount,
  toColumnGroupOptions,
  toEditModalTexts,
  toHistoryFilter,
  toHistoryRow,
  toMatrixFilter,
  toMatrixRow,
  toSaveRequest,
  toSelectedCell,
  toUploadSingleStartDate,
  toValidationView,
} from '@/services/global/gpcRate/mapping'
import type { GpcRateSelectedCell } from '@/services/global/gpcRate/mapping'
import {
  GPC_RATE_CONFLICT_ERROR_CODE,
  GPC_RATE_EXCEL_ERROR_CODES,
  GPC_RATE_FORBIDDEN_STATUS,
  GPC_RATE_HISTORY_MAX_SIZE,
  GPC_RATE_MATRIX_MAX_SIZE,
} from '@/services/global/gpcRate/types'
import type { GpcRateExcelValidation, GpcRateMatrixFilter, GpcRateSaveReq } from '@/services/global/gpcRate/types'
import {
  refreshGpcRateHistories,
  refreshGpcRateMatrix,
  useGpcRateHistoriesQuery,
  useGpcRateMatrixQuery,
  useSaveGpcRatePriceMutation,
} from '@/services/global/gpcRate/useGpcRateQueries'
import { gpcRateEditSchema, toEditForm } from './schemas/gpcRateEditSchema'
import type { Option, Row, ValidateView, Wiring } from './index'

/** 한 페이지 행(카테고리) 수 — BE 기본 100·상한 1,000. 생성본 페이저는 '몇 개씩' 을 숨기므로 이 값이 고정이다. */
export const GPC_RATE_PAGE_SIZE = 100

/** 변경 이력 한 페이지 행 수 — BE 상한 100(생성본 이력 페이저 기본값과 같다). */
export const GPC_RATE_HISTORY_PAGE_SIZE = GPC_RATE_HISTORY_MAX_SIZE

/** 1,000행 × 15개 국가 그룹 양식에 충분한 상한. 대용량 워크북이 서버 메모리에 올라가기 전에 화면에서 차단한다. */
export const GPC_RATE_EXCEL_MAX_FILE_SIZE = 10 * 1000 * 1000

/** 화면 문구 — PRD 화면 validation 번호를 붙인 것은 원문 그대로다(No.2·3·5·6 은 서버 문구를 그대로 띄운다). */
export const GPC_RATE_MESSAGES = {
  basisDateRequired: '기준일자를 선택해 주세요.',
  cellNotSelected: '수정할 단가 셀을 다시 선택해 주세요.',
  zeroPriceConfirm: '단가를 0원으로 저장하면 해당 조합에는 분담금이 부과되지 않습니다. 저장하시겠습니까?', // No.1
  saveFailed: '단가 저장에 실패했습니다.',
  saveConflict: '다른 사용자가 먼저 저장했습니다. 최신 내용을 불러온 뒤 다시 시도해 주세요.', // No.7
  noPermission: '단가 등록·수정 권한이 없습니다. 조회만 가능합니다.', // No.8
  uploadErrorBlocked: (count: number) => `오류 ${count.toLocaleString('ko-KR')}건이 있어 저장할 수 없습니다. 오류를 모두 수정한 뒤 다시 업로드해 주세요.`, // No.10
  unregisteredWarning: (count: number) =>
    `단가가 등록되지 않은 조합이 ${count.toLocaleString('ko-KR')}건 있습니다. 해당 조합의 입고분은 기타 카테고리 단가로 청구됩니다.`, // No.11
  saveSucceeded: (startDate: string) => `단가가 저장되었습니다. ${startDate} 입고분부터 적용됩니다.`, // No.14
  saveSucceededPerRow: '단가가 저장되었습니다. 행별 적용 시작일 입고분부터 적용됩니다.', // No.14 — 엑셀 행마다 적용 시작일이 다를 때
  uploadFileRequired: '업로드할 엑셀 파일을 선택해 주세요.',
  uploadFileTooLarge: '업로드 파일은 10MB 이하만 가능합니다.',
  uploadValidateFailed: '엑셀 파일 검증에 실패했습니다.',
  uploadNotValidated: '엑셀 파일을 다시 업로드해 주세요.',
  uploadNoChange: '변경할 단가가 없습니다.',
  uploadSaveFailed: '엑셀 단가 저장에 실패했습니다.',
  downloadFailed: '엑셀 다운로드에 실패했습니다.',
} as const

/** 저장 실패 응답에서 사용자 문구(meta.message)를 꺼낸다 — Utils.getApiErrMessage 는 HTML 별표를 덧붙여 쓰지 않는다. */
export const extractApiMessage = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { meta?: { message?: unknown } } } })?.response?.data?.meta?.message
  return typeof message === 'string' && message.trim() ? message : fallback
}

/** 409(동시 편집 충돌) 판정 — HTTP 상태 또는 meta.errorCode CONFLICT. */
export const isConflictError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 409 || Utils.getApiErrorCode(error) === GPC_RATE_CONFLICT_ERROR_CODE

/** 403 — MOMS 게이트웨이 사용자가 아니면 저장·업로드가 막힌다(PRD No.8). */
export const isForbiddenError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === GPC_RATE_FORBIDDEN_STATUS

/** 엑셀 확정 저장 409 중 검증 오류(data = 그 시점 검증 결과)면 그 결과를, 아니면 null(동시 저장 충돌 등). */
export const pickValidationFailure = (error: unknown): GpcRateExcelValidation | null => {
  const code = Utils.getApiErrorCode(error)
  if (code !== GPC_RATE_EXCEL_ERROR_CODES.partialValidationFailure && code !== GPC_RATE_EXCEL_ERROR_CODES.totalValidationFailure) return null
  const data = (error as { response?: { data?: { data?: unknown } } })?.response?.data?.data as GpcRateExcelValidation | undefined
  return data?.summary ? data : null
}

/** 확인창을 Promise 로 — 확인이면 true, 닫기면 false. */
const confirmAsync = (content: string): Promise<boolean> =>
  new Promise((resolve) => {
    MConfirm.show(content, { onConfirm: () => resolve(true), onClose: () => resolve(false) })
  })

/** 알럿을 띄우고 실패로 끝낸다 — 생성본은 throw 를 받아 모달을 열어 둔다(실패 사유는 여기서만 보인다). */
const fail = (message: string): never => {
  MAlert.show(message)
  throw new Error(message)
}

// 모듈 변수 — rows.ROWS(렌더)가 채우고 actions.*(이벤트)가 읽는다. ROWS 의 마운트 effect 가 비운다(⑤).
let commitFilter: ((next: GpcRateMatrixFilter | null) => void) | null = null
let committedFilter: GpcRateMatrixFilter | null = null
let queryClientRef: QueryClient | null = null
let savePrice: ((body: GpcRateSaveReq) => Promise<unknown>) | null = null
let selectedCell: GpcRateSelectedCell | null = null
// 엑셀 업로드 — 고른 파일(확정 저장 때 다시 보낸다)·검증 결과. ROWS2 의 마운트 effect 가 비우고 setter 로 검증 모달을 다시 그린다.
let uploadFile: Blob | null = null
let uploadFileName = ''
let uploadValidation: GpcRateExcelValidation | null = null
let showValidation: ((next: GpcRateExcelValidation | null) => void) | null = null
// 변경 이력 — [변경 이력]을 처음 누를 때 조회를 켠다(ROWS3 의 상태 setter).
let enableHistories: (() => void) | null = null

/** 검증 결과를 모듈 변수에 두고 검증 모달을 다시 그린다. */
const setValidation = (next: GpcRateExcelValidation | null) => {
  uploadValidation = next
  showValidation?.(next)
}

/** 검증 결과 → 검증 모달 슬롯 값 — rows = 오류 행, validation = 건수·변경 예정 셀 + 오류 차단(No.10)/미등록 경고(No.11) 문구. */
const toValidateSlot = (v: GpcRateExcelValidation): { rows: Row[]; total: number; validation: ValidateView } => {
  const { errorRows, ...view } = toValidationView(v, uploadFileName)
  const unregistered = gpcRateUnregisteredWarningCount(v.summary)
  return {
    rows: errorRows as Row[],
    total: errorRows.length,
    validation: {
      ...view,
      changeRows: view.changeRows as Row[],
      ...(v.summary.error > 0 ? { errorNotice: GPC_RATE_MESSAGES.uploadErrorBlocked(v.summary.error) } : {}),
      ...(unregistered > 0 ? { warningNotice: GPC_RATE_MESSAGES.unregisteredWarning(unregistered) } : {}),
    },
  }
}
const EMPTY_VALIDATE_SLOT = { rows: [] as Row[], validation: { fileText: '', changed: 0, unchanged: 0, unregistered: 0, error: 0, changeRows: [] } satisfies ValidateView }

/** 파일 여부 — 생성본 드래거가 폼 버스에 File(originFileObj)을 올린다. */
const asUploadFile = (v: unknown): Blob | null => (v instanceof Blob ? v : null)

/** xlsx 다운로드 — 실패는 서버 문구(meta.message) 알럿. */
const runDownload = async (url: string, fallbackName: string) => {
  try {
    await downloadGpcRateExcel(url, fallbackName)
  } catch (error) {
    MAlert.show(extractApiMessage(error, GPC_RATE_MESSAGES.downloadFailed))
  }
}

export const wiring: Wiring = {
  // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
  columnLayout: { gpcRateTable: coreTableLayoutSlot('GL16_HEAD') },
  rows: {
    /**
     * 매트릭스 — 서버 페이징 { rows, total, loading, columnGroups }. 조회 조건은 [조회] 가 커밋한 값(committed)이고 페이지는 생성본 페이저다.
     * 진입 시엔 조회영역 기본값(기준일 = 오늘)으로 바로 조회한다 — 필수 조건이 이미 채워져 있어 [조회] 를 기다릴 이유가 없다.
     * 행 key 는 logisticsCategoryCode(toMatrixRow) — 생성본 `{ key: i, ...r }` 스프레드가 인덱스를 덮는다.
     */
    ROWS: (_demo, q) => {
      const queryClient = useQueryClient()
      const { mutateAsync } = useSaveGpcRatePriceMutation()
      const [filter, setFilter] = useState<GpcRateMatrixFilter | null>(() => toMatrixFilter(q.filters))
      useEffect(() => {
        commitFilter = setFilter
        selectedCell = null
        return () => {
          commitFilter = null
          committedFilter = null
          queryClientRef = null
          savePrice = null
          selectedCell = null
          uploadFile = null
          uploadFileName = ''
          uploadValidation = null
        }
      }, [])
      committedFilter = filter
      queryClientRef = queryClient
      savePrice = mutateAsync

      const page = q.pager?.page ?? 1
      const size = Math.min(q.pager?.size ?? GPC_RATE_PAGE_SIZE, GPC_RATE_MATRIX_MAX_SIZE)
      const { data, isFetching } = useGpcRateMatrixQuery(filter, page, size)

      const rows = useMemo(() => (data?.items ?? []).map(toMatrixRow) as unknown as Row[], [data])
      const columnGroups = useMemo(() => toColumnGroupOptions(data?.countryGroups) as Option[] | undefined, [data])
      return { rows, total: data?.pageInfo?.totalCount ?? 0, loading: isFetching, columnGroups }
    },
    /**
     * 엑셀 업로드 검증 결과 — rows = 오류 행, validation = 건수·변경 예정 셀·문구. 검증 전(한 번도 업로드 안 함)은 빈 결과.
     * 검증 결과는 업로드 모달 [업로드](검증 API)와 검증 모달 [저장]의 409(저장 시점 재검증)가 setter 로 올린다.
     */
    ROWS2: () => {
      const [validation, setValidationState] = useState<GpcRateExcelValidation | null>(null)
      useEffect(() => {
        showValidation = setValidationState
        return () => {
          showValidation = null
        }
      }, [])
      return useMemo(() => (validation ? toValidateSlot(validation) : EMPTY_VALIDATE_SLOT), [validation])
    },
    /**
     * 변경 이력 — 서버 페이징(size 최대 100). 이력 모달 필터(물류 카테고리·국가)·페이저는 생성본이 q 로 준다.
     * [변경 이력]을 처음 누르기 전에는 조회하지 않는다(opens.historyButton 이 켠다).
     */
    ROWS3: (_demo, q) => {
      const [enabled, setEnabled] = useState(false)
      useEffect(() => {
        enableHistories = () => setEnabled(true)
        return () => {
          enableHistories = null
        }
      }, [])
      const filter = useMemo(() => toHistoryFilter(q.filters), [q.filters])
      const page = q.pager?.page ?? 1
      const size = Math.min(q.pager?.size ?? GPC_RATE_HISTORY_PAGE_SIZE, GPC_RATE_HISTORY_MAX_SIZE)
      const { data, isFetching } = useGpcRateHistoriesQuery(filter, page, size, enabled)
      const rows = useMemo(() => (data?.items ?? []).map(toHistoryRow) as Row[], [data])
      return { rows, total: data?.pageInfo?.totalCount ?? 0, loading: isFetching }
    },
  },
  actions: {
    /** [조회] — 조회영역 값을 조건으로 커밋한다. 같은 조건이면 queryKey 가 같아 요청이 안 나가므로 무효화로 다시 받는다(⑥). */
    search: (filters) => {
      const next = toMatrixFilter(filters)
      if (!next) {
        MAlert.show(GPC_RATE_MESSAGES.basisDateRequired)
        return
      }
      if (isEqual(next, committedFilter)) {
        if (queryClientRef) refreshGpcRateMatrix(queryClientRef).catch(() => undefined)
        return
      }
      commitFilter?.(next)
    },
    /** 단가 셀 클릭 — action = 국가 그룹 코드, row = 표 행. 선택 셀을 모듈 변수에 두고 생성본이 단가 수정 모달을 연다. */
    ROWS_rowAction: (action, row) => {
      selectedCell = toSelectedCell(row, action)
    },
    /**
     * 단가 수정 모달 [저장] — 필수값·형식·상한(100,000 미만)은 zod, 0원은 확인창, 걸치는 기존 행의 id·version 은 toSaveRequest(findBasePrice) 가 싣는다.
     * 400 은 meta.message 를 알럿으로, 409 는 충돌 안내 + 매트릭스 재조회. 실패는 throw(모달 유지). 성공 문구를 돌려주면 생성본이 그 문구로 토스트·닫기.
     */
    gpcRateEditModal_ok: async (ctx) => {
      const selected = selectedCell
      if (!selected) return fail(GPC_RATE_MESSAGES.cellNotSelected)
      const parsed = gpcRateEditSchema.safeParse(toEditForm(ctx.form ?? {}))
      if (!parsed.success) return fail(parsed.error.issues[0]?.message ?? GPC_RATE_MESSAGES.saveFailed)
      const { price, startDate } = parsed.data
      if (price === 0 && !(await confirmAsync(GPC_RATE_MESSAGES.zeroPriceConfirm))) throw new Error('cancelled') // 알럿 없이 모달만 유지
      if (!savePrice) return fail(GPC_RATE_MESSAGES.saveFailed)
      try {
        await savePrice(toSaveRequest(selected, { price, startDate }))
      } catch (error) {
        if (isForbiddenError(error)) return fail(GPC_RATE_MESSAGES.noPermission)
        if (isConflictError(error)) {
          if (queryClientRef) refreshGpcRateMatrix(queryClientRef).catch(() => undefined)
          return fail(GPC_RATE_MESSAGES.saveConflict)
        }
        return fail(extractApiMessage(error, GPC_RATE_MESSAGES.saveFailed))
      }
      return GPC_RATE_MESSAGES.saveSucceeded(startDate)
    },
    /** [양식 다운로드] — 화면 기준일(조회에 커밋된 값)의 단가가 채워진 업로드 양식. */
    templateButton: async () => {
      const basisDate = committedFilter?.basisDate
      if (!basisDate) {
        MAlert.show(GPC_RATE_MESSAGES.basisDateRequired)
        return
      }
      await runDownload(gpcRateExcelTemplateUrl(basisDate), gpcRateExcelFallbackName('양식', basisDate))
    },
    /** [엑셀 다운로드] — 지금 조회 조건(기준일·국가·물류 카테고리) 그대로 전 행. 행 선택과 무관하다(티켓 확정 — 핸드오프 README 의 '선택 연동' 과 다름). */
    excelDownloadButton: async () => {
      const filter = committedFilter
      if (!filter) {
        MAlert.show(GPC_RATE_MESSAGES.basisDateRequired)
        return
      }
      await runDownload(gpcRateExcelSearchResultUrl(filter), gpcRateExcelFallbackName('조회결과', filter.basisDate))
    },
    /**
     * 엑셀 업로드 모달 [업로드] — 서버 검증 미리보기. 성공하면 false(토스트 없음)를 돌려주고 생성본이 검증 모달을 연다.
     * 파일 단위 오류(400 INVALID_EXCEL_FILE · EXCEL_ROW_COUNT_LIMIT_EXCEEDED)는 서버 문구 알럿 + throw(업로드 모달 유지).
     */
    gpcRateUploadModal_ok: async (ctx) => {
      const file = asUploadFile(ctx.form?.file)
      if (!file) return fail(GPC_RATE_MESSAGES.uploadFileRequired)
      if (file.size > GPC_RATE_EXCEL_MAX_FILE_SIZE) return fail(GPC_RATE_MESSAGES.uploadFileTooLarge)
      let validation: GpcRateExcelValidation
      try {
        validation = (await validateGpcRateExcel(file)).data
      } catch (error) {
        if (isForbiddenError(error)) return fail(GPC_RATE_MESSAGES.noPermission)
        return fail(extractApiMessage(error, GPC_RATE_MESSAGES.uploadValidateFailed))
      }
      uploadFile = file
      uploadFileName = file instanceof File ? file.name : ''
      setValidation(validation)
      return false as const
    },
    /**
     * 검증 모달 [저장] — 파일만 보낸다(적용 기간은 엑셀 행의 시작일·종료일). 오류 1건 이상이면 요청 없이 차단(No.10),
     * 0원으로 바뀌는 셀이 있으면 0원 확인창(No.1), 미등록 조합이 남으면 경고 확인창(No.11) 뒤 저장.
     * 409 검증 오류는 그 시점 검증 결과로 모달을 다시 그리고 서버 문구 알럿, 409 충돌은 No.7 + 매트릭스 재조회, 403 은 No.8.
     * 성공 = No.14 문구(적용 시작일이 하나면 그 날짜, 여럿이면 행별 문구 — 생성본이 토스트·닫기) + 매트릭스 재조회.
     */
    gpcRateValidateModal_ok: async () => {
      const file = uploadFile
      const validation = uploadValidation
      if (!file || !validation) return fail(GPC_RATE_MESSAGES.uploadNotValidated)
      const block = gpcRateUploadSaveBlock(validation.summary)
      if (block === 'ERROR') return fail(GPC_RATE_MESSAGES.uploadErrorBlocked(validation.summary.error))
      if (block === 'NO_CHANGE') return fail(GPC_RATE_MESSAGES.uploadNoChange)
      if (gpcRateZeroPriceCount(validation.summary, validation.changes) > 0 && !(await confirmAsync(GPC_RATE_MESSAGES.zeroPriceConfirm))) throw new Error('cancelled') // 알럿 없이 모달만 유지
      const unregistered = gpcRateUnregisteredWarningCount(validation.summary)
      if (unregistered > 0 && !(await confirmAsync(GPC_RATE_MESSAGES.unregisteredWarning(unregistered)))) throw new Error('cancelled') // 알럿 없이 모달만 유지
      let savedStartDate: string | null
      try {
        savedStartDate = toUploadSingleStartDate((await uploadGpcRateExcel(file)).data, validation.changes)
      } catch (error) {
        if (isForbiddenError(error)) return fail(GPC_RATE_MESSAGES.noPermission)
        const revalidated = pickValidationFailure(error)
        if (revalidated) {
          setValidation(revalidated)
          return fail(extractApiMessage(error, GPC_RATE_MESSAGES.uploadErrorBlocked(revalidated.summary.error)))
        }
        if (isConflictError(error)) {
          if (queryClientRef) refreshGpcRateMatrix(queryClientRef).catch(() => undefined)
          return fail(GPC_RATE_MESSAGES.saveConflict)
        }
        return fail(extractApiMessage(error, GPC_RATE_MESSAGES.uploadSaveFailed))
      }
      if (queryClientRef) refreshGpcRateMatrix(queryClientRef).catch(() => undefined)
      uploadFile = null // 같은 파일로 두 번 저장하지 않게 — 검증 결과는 닫히는 모달이 깜빡이지 않게 그대로 둔다
      return savedStartDate ? GPC_RATE_MESSAGES.saveSucceeded(savedStartDate) : GPC_RATE_MESSAGES.saveSucceededPerRow
    },
    /** 이력 모달은 [닫기]만 있다(생성본이 확인 버튼을 숨긴다) — 불리지 않는다. */
    gpcRateHistoryModal_ok: async () => {},
  },
  opens: {
    /** [변경 이력] — 처음 열 때 조회를 켜고, 다시 열 때는 최신으로 다시 받는다(닫혀 있는 동안 저장됐을 수 있다). */
    historyButton: () => {
      enableHistories?.()
      if (queryClientRef) refreshGpcRateHistories(queryClientRef).catch(() => undefined)
      return true
    },
  },
  form: {
    /** 단가 수정 모달 참고 정보(카테고리·국가 그룹·현재/예약 단가) — 선택 셀에서 파생해 폼 버스에 덮어쓴다. 입력값(price·startDate)은 생성본이 모은다. */
    gpcRateEditModal: (state) => ({ ...state, ...toEditModalTexts(selectedCell) }),
    gpcRateUploadModal: (state) => state,
  },
  options: {
    /** 국가 = 단가 축 국가 그룹 15개(BE 선언 순서, KR 없음). 데모 국가 26개는 열과 맞지 않는다. */
    COUNTRYSELECT_OPTIONS: () => GPC_RATE_COUNTRY_GROUP_OPTIONS as Option[],
    /** 물류 카테고리 = moms BE 공통코드(category####). 값이 코드라 조회 조건(logisticsCategoryCodes)으로 그대로 나간다.
     *  응답 전·빈 응답이면 빈 목록 — 데모(의류·신발…)는 코드가 아니라 조회 조건으로 나가면 안 된다. */
    LOGICATEGORYSELECT_OPTIONS: () => {
      const { data } = useGetLogisticsCategories()
      return (data ?? []).map((c) => ({ value: c.code, label: c.name ? `${c.name} (${c.code})` : c.code }))
    },
  },
  /** 조회 조건 기본값 — 기준일자 = 오늘(핸드오프 노트 ★). 국가·카테고리는 '전체'. */
  filterDefaults: {
    gpcRateForm: (demo) => ({ ...demo, [GPC_RATE_FILTER_KEYS.basisDate]: dayjs().format('YYYY-MM-DD') }),
  },
  /** 페이저 초기값 — 데모 10,000 은 BE 상한(1,000)을 넘는다. 한 페이지 100행(BE 기본)으로 서버 페이지네이션. */
  stateInitial: { pager: { pager: { page: 1, size: GPC_RATE_PAGE_SIZE } } },
}
